import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";

const POLL_INTERVAL_MS = 30000; // 30 seconds - faster checking

interface PollerCallbacks {
  onProcessed?: (count: number, results: Array<{ historyId: string; status: string }>) => void;
  onError?: (message: string) => void;
  onRefreshNeeded?: () => void;
}

export function useScheduledSendPoller(callbacks?: PollerCallbacks) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const processingRef = useRef(false);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const processScheduled = useCallback(async () => {
    if (processingRef.current) return; // prevent overlapping calls
    processingRef.current = true;

    try {
      const { data, error } = await supabase.functions.invoke("process-scheduled-sends", {});

      if (error) {
        // 백그라운드 폴링 실패는 일시적일 수 있으므로 조용히 경고만 남기고 다음 주기에 재시도
        console.warn("[ScheduledPoller] 예약 발송 처리 일시 실패:", error?.message || error);
        return;
      }

      if (data?.processed > 0) {
        console.log(`[ScheduledPoller] ✅ Processed ${data.processed} scheduled send(s):`, data.results);
        callbacksRef.current?.onProcessed?.(data.processed, data.results);
        callbacksRef.current?.onRefreshNeeded?.();
      }

      // Also drive background split-send processing so a closed page never loses progress.
      try {
        const { data: splitData, error: splitError } = await supabase.functions.invoke("process-split-sends", {});
        if (!splitError && splitData?.processed > 0) {
          console.log(`[ScheduledPoller] ✅ Processed ${splitData.processed} split send(s)`);
          callbacksRef.current?.onRefreshNeeded?.();
        }
      } catch (splitErr: any) {
        console.warn("[ScheduledPoller] 분할 발송 처리 일시 실패:", splitErr?.message || splitErr);
      }
    } catch (err: any) {
      console.warn("[ScheduledPoller] 예약 발송 처리 실패:", err?.message || err);
    } finally {
      processingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Initial call - slight delay to let page settle
    const initialTimer = setTimeout(processScheduled, 1500);

    // Set up interval
    intervalRef.current = setInterval(processScheduled, POLL_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimer);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [processScheduled]);

  // Expose manual trigger
  return { triggerNow: processScheduled };
}